import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Header from "./Header";

const BASE_URL =
  "https://522gqh3w18.execute-api.ap-south-1.amazonaws.com/Prod/Symbol";

const EXCHANGES = ["NSE", "BSE", "MCX", "NFO", "BFO", "CDS"];

const newRow = () => ({
  _key: crypto.randomUUID(),
  symbol: "",
  exchange: "NSE",
});

function AddSymbol() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([newRow()]);
  const [submitting, setSubmitting] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [bulkMode, setBulkMode] = useState(false);

  // ── Row helpers ───────────────────────────────────────────────────────────
  const setRowField = (key, field, val) =>
    setRows((p) =>
      p.map((r) => (r._key === key ? { ...r, [field]: val } : r))
    );

  const addRow = () => setRows((p) => [...p, newRow()]);

  const removeRow = (key) =>
    setRows((p) => p.filter((r) => r._key !== key));

  // Auto-detect exchange from symbol string e.g. "NSE:HDFC-EQ" → "NSE"
  const handleSymbolChange = (key, val) => {
    const upper = val.toUpperCase();
    const autoExchange = upper.split(":")?.[0];
    const isKnown = EXCHANGES.includes(autoExchange);
    setRows((p) =>
      p.map((r) =>
        r._key === key
          ? { ...r, symbol: upper, ...(isKnown && { exchange: autoExchange }) }
          : r
      )
    );
  };

  // ── Bulk paste parse ──────────────────────────────────────────────────────
  // Accepts newline or comma-separated symbol strings e.g. "NSE:HDFC-EQ"
  const parseBulk = () => {
    const lines = bulkText
      .split(/[\n,]+/)
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean);

    if (!lines.length) return;

    const parsed = lines.map((sym) => {
      const autoExchange = sym.split(":")?.[0];
      return {
        _key: crypto.randomUUID(),
        symbol: sym,
        exchange: EXCHANGES.includes(autoExchange) ? autoExchange : "NSE",
      };
    });

    setRows(parsed);
    setBulkMode(false);
    setBulkText("");
  };

  // ── Submit ────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!e.target.checkValidity()) {
      e.target.classList.add("was-validated");
      return;
    }

    const validRows = rows.filter((r) => r.symbol.trim());
    if (!validRows.length) {
      await Swal.fire({
        title: "No Symbols",
        text: "Please enter at least one symbol before submitting.",
        icon: "info",
        confirmButtonColor: "#0d6efd",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
      return;
    }

    const payload = validRows.map(({ symbol, exchange }) => ({
      symbol: symbol.trim(),
      exchange: exchange.trim(),
    }));

    console.log("📤 AddSymbols payload:", payload);
    setSubmitting(true);

    try {
      const res = await fetch(`${BASE_URL}/AddSymbols`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("SLNAdminToken") ?? "",
        },
        body: JSON.stringify(payload),
      });

      const data = await res.json().catch(() => ({}));
      console.log("✅ AddSymbols response:", data);
      if (!res.ok) throw new Error(data?.message ?? `HTTP ${res.status}`);

      await Swal.fire({
        title: "Symbols Added!",
        text:
          data?.message ??
          `${payload.length} symbol${payload.length > 1 ? "s" : ""} added successfully.`,
        icon: "success",
        confirmButtonColor: "#198754",
        confirmButtonText: "Go to Symbols",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });

      navigate("/manage/all-symbols");
    } catch (err) {
      console.error("❌ AddSymbols error:", err);
      await Swal.fire({
        title: "Failed to Add",
        text: err.message || "Something went wrong.",
        icon: "error",
        confirmButtonColor: "#dc3545",
        customClass: { popup: "rounded-4", confirmButton: "px-4 rounded-3" },
      });
    } finally {
      setSubmitting(false);
    }
  };

  // ── UI helpers ────────────────────────────────────────────────────────────
  const SectionHead = ({ icon, title }) => (
    <div className="col-12 mt-4 mb-1">
      <h6 className="fw-semibold text-dark d-flex align-items-center gap-2 mb-0">
        <i className={`bi ${icon} text-primary`}></i> {title}
      </h6>
      <hr className="mt-1 mb-0" />
    </div>
  );

  const exchangeBadgeColor = (ex) => {
    const map = {
      NSE: "#0d6efd",
      BSE: "#0dcaf0",
      MCX: "#ffc107",
      NFO: "#6f42c1",
      BFO: "#fd7e14",
      CDS: "#20c997",
    };
    return map[ex] ?? "#6c757d";
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <Header />

      <section>
        <div className="container py-3">
          <div className="row">
            <div className="col-12">
              <div className="pt-1 d-flex flex-column flex-md-row gap-3 align-items-md-center justify-content-between">
                <div>
                  <h4 className="mb-0 lh-1 fw-medium text-dark">
                    Add Symbols
                  </h4>
                  <p className="m-0 lh-sm text-secondary ls-sm">
                    Add trading symbols to the watchlist
                  </p>
                </div>
                <div>
                  <span
                    onClick={() => navigate(-1)}
                    className="btn btn-outline-secondary btn-sm px-3 rounded-pill"
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-arrow-left me-1"></i> Back
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div className="mb-6">
            <form
              className="row g-2 needs-validation"
              noValidate
              onSubmit={handleSubmit}
            >
              {/* ── Input Mode Toggle ── */}
              <SectionHead icon="bi-graph-up" title="Symbol Entries" />

              <div className="col-12">
                <div className="border rounded-4 p-4 bg-light">

                  {/* Mode switcher */}
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="small text-secondary fw-semibold me-1">
                      Input Mode:
                    </span>
                    <button
                      type="button"
                      className={`btn btn-sm px-3 rounded-3 ${!bulkMode ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setBulkMode(false)}
                    >
                      <i className="bi bi-ui-checks me-1"></i> Row by Row
                    </button>
                    <button
                      type="button"
                      className={`btn btn-sm px-3 rounded-3 ${bulkMode ? "btn-primary" : "btn-outline-secondary"}`}
                      onClick={() => setBulkMode(true)}
                    >
                      <i className="bi bi-textarea-t me-1"></i> Bulk Paste
                    </button>
                  </div>

                  {/* ── Bulk Paste Mode ── */}
                  {bulkMode ? (
                    <div className="row g-2">
                      <div className="col-lg-6 col-12">
                        <label className="form-label small">
                          Paste Symbols
                          <span className="text-secondary fw-normal ms-2">
                            (newline or comma separated)
                          </span>
                        </label>
                        <textarea
                          className="form-control form-control-sm"
                          rows={8}
                          placeholder={`NSE:HDFCBANK-EQ\nNSE:SBIN-EQ\nNSE:INFY-EQ`}
                          value={bulkText}
                          onChange={(e) =>
                            setBulkText(e.target.value.toUpperCase())
                          }
                          style={{ fontFamily: "monospace", fontSize: 13 }}
                        />
                        <div className="form-text">
                          Exchange auto-detected from prefix (e.g. NSE:, BSE:).
                          Defaults to NSE if not detected.
                        </div>
                      </div>
                      <div className="col-12">
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary rounded-3"
                          onClick={parseBulk}
                          disabled={!bulkText.trim()}
                        >
                          <i className="bi bi-arrow-right-circle me-1"></i>
                          Parse & Preview Rows
                        </button>
                      </div>

                      {/* Preview parsed rows */}
                      {rows.length > 0 && rows[0].symbol && (
                        <div className="col-12 mt-2">
                          <p className="small text-secondary mb-2">
                            <i className="bi bi-eye me-1"></i>
                            Preview — {rows.length} symbol
                            {rows.length > 1 ? "s" : ""} parsed:
                          </p>
                          <div className="d-flex flex-wrap gap-2">
                            {rows.map((r) => (
                              <span
                                key={r._key}
                                className="badge border px-2 py-1 rounded-3 d-inline-flex align-items-center gap-1"
                                style={{
                                  background: "#fff",
                                  color: "#212529",
                                  borderColor: "#dee2e6",
                                }}
                              >
                                <span
                                  className="badge rounded-2 me-1"
                                  style={{
                                    background: exchangeBadgeColor(r.exchange),
                                    fontSize: 10,
                                  }}
                                >
                                  {r.exchange}
                                </span>
                                {r.symbol}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    /* ── Row by Row Mode ── */
                    <div>
                      {/* Column headers */}
                      <div className="row g-2 mb-1 d-none d-md-flex">
                        <div className="col-md-6">
                          <span className="form-label small mb-0">
                            Symbol{" "}
                            <span className="text-danger fw-bold">*</span>
                          </span>
                        </div>
                        <div className="col-md-3">
                          <span className="form-label small mb-0">
                            Exchange{" "}
                            <span className="text-danger fw-bold">*</span>
                          </span>
                        </div>
                      </div>

                      {rows.map((row, idx) => (
                        <div className="row g-2 mb-2 align-items-center" key={row._key}>
                          {/* Symbol input */}
                          <div className="col-md-6 col-12">
                            {idx === 0 && (
                              <label className="form-label small d-md-none">
                                Symbol{" "}
                                <span className="text-danger fw-bold">*</span>
                              </label>
                            )}
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="e.g. NSE:HDFCBANK-EQ"
                              value={row.symbol}
                              onChange={(e) =>
                                handleSymbolChange(row._key, e.target.value)
                              }
                              required
                              style={{
                                fontFamily: "monospace",
                                fontWeight: 500,
                                letterSpacing: "0.02em",
                              }}
                            />
                            <div className="invalid-feedback">
                              Please enter a symbol.
                            </div>
                          </div>

                          {/* Exchange select */}
                          <div className="col-md-3 col-8">
                            {idx === 0 && (
                              <label className="form-label small d-md-none">
                                Exchange{" "}
                                <span className="text-danger fw-bold">*</span>
                              </label>
                            )}
                            <select
                              className="form-select form-select-sm"
                              value={row.exchange}
                              onChange={(e) =>
                                setRowField(row._key, "exchange", e.target.value)
                              }
                              required
                            >
                              {EXCHANGES.map((ex) => (
                                <option key={ex} value={ex}>
                                  {ex}
                                </option>
                              ))}
                            </select>
                          </div>

                          {/* Exchange colour dot */}
                          <div className="col-auto d-none d-md-flex align-items-center">
                            <span
                              className="badge rounded-3 px-2 py-1"
                              style={{
                                background: exchangeBadgeColor(row.exchange),
                                fontSize: 11,
                                minWidth: 40,
                              }}
                            >
                              {row.exchange}
                            </span>
                          </div>

                          {/* Remove */}
                          <div className="col-auto">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger rounded-circle p-0 ms-1"
                              style={{ width: 28, height: 28, lineHeight: 1 }}
                              onClick={() => removeRow(row._key)}
                              disabled={rows.length === 1}
                              title="Remove row"
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          </div>
                        </div>
                      ))}

                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary rounded-3 mt-1"
                        onClick={addRow}
                      >
                        <i className="bi bi-plus-lg me-1"></i> Add Row
                      </button>

                      {/* Count indicator */}
                      {rows.length > 1 && (
                        <span className="text-secondary small ms-3">
                          {rows.filter((r) => r.symbol.trim()).length} /{" "}
                          {rows.length} filled
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Submit ── */}
              <div className="col-12 mt-4">
                <button
                  className="btn btn-success px-6 fw-normal py-1 fs-5 ls-sm d-inline-flex align-items-center gap-2"
                  type="submit"
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm"
                        role="status"
                      />
                      Adding…
                    </>
                  ) : (
                    <>
                      <i className="bi bi-cloud-upload"></i>
                      Add{" "}
                      {rows.filter((r) => r.symbol.trim()).length > 0
                        ? `${rows.filter((r) => r.symbol.trim()).length} Symbol${rows.filter((r) => r.symbol.trim()).length > 1 ? "s" : ""}`
                        : "Symbols"}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>
    </>
  );
}

export default AddSymbol;
